class AccordionElement extends HTMLElement {
  constructor() {
    super();
    this.details = this.closest("details");
    this.summary = this.details.querySelector("summary");

    this._resetHeight();

    if (!this.id) {
      this.id = `accordion-${Math.random().toString(36).slice(2)}`;
    }

    this.summary.setAttribute("aria-controls", this.id);
    this.summary.setAttribute(
      "aria-expanded",
      this.details.open ? "true" : "false",
    );

    // Bind event handlers
    this.onClick = this.onClick.bind(this);
    this.onDetailsClick = this.onDetailsClick.bind(this);
    this.onResize = this.onResize.bind(this);

    // Bind click handler to the full details block for a larger tap target.
    this.details.addEventListener("click", this.onDetailsClick);

    // Listen for window resize event
    window.addEventListener("resize", this.onResize);
  }

  _getDetailsVerticalPadding() {
    const styles = window.getComputedStyle(this.details);
    return (
      parseFloat(styles.paddingTop || "0") +
      parseFloat(styles.paddingBottom || "0")
    );
  }

  _getClosedHeight() {
    return this.summary.scrollHeight + this._getDetailsVerticalPadding() - 3;
  }

  _getOpenHeight() {
    return this.details.scrollHeight;
  }

  _closeOtherAccordions() {
    const accordionElements = document.querySelectorAll(
      "accordion-element[data-accordion-content]",
    );

    accordionElements.forEach((accordionElement) => {
      if (accordionElement !== this && accordionElement.details?.open) {
        accordionElement._close();
      }
    });
  }

  _open() {
    this._closeOtherAccordions();

    this.details.setAttribute("data-opening", "true");
    this.details.setAttribute("open", "true");

    // --- ADD ---
    this.summary.setAttribute("aria-expanded", "true");
    // --- END ---

    this.details.style.height = `${this._getOpenHeight()}px`;
  }

  _close() {
    this.details.removeAttribute("data-opening");
    this.details.setAttribute("data-closing", "true");

    // --- ADD ---
    this.summary.setAttribute("aria-expanded", "false");
    // --- END ---

    setTimeout(() => {
      this.details.removeAttribute("open");
      this.details.removeAttribute("data-closing");
    }, 300);

    this.details.style.height = `${this._getClosedHeight()}px`;
  }

  _resetHeight() {
    this.details.style.height = `${this._getClosedHeight()}px`;
  }

  onClick(e) {
    e.preventDefault();
    if (!this.details.open) {
      this._open();
    } else {
      this._close();
    }
  }

  onDetailsClick(e) {
    const interactiveTarget = e.target.closest(
      "a, button, input, select, textarea, label",
    );

    if (interactiveTarget) {
      return;
    }

    this.onClick(e);
  }

  onResize() {
    if (this.details.open) {
      this.details.style.height = `${this._getOpenHeight()}px`;
    } else {
      this._resetHeight();
    }
  }

  // Clean up event listener when element is removed from the DOM
  disconnectedCallback() {
    this.details.removeEventListener("click", this.onDetailsClick);
    window.removeEventListener("resize", this.onResize);
  }
}

if (!customElements.get("accordion-element")) {
  customElements.define("accordion-element", AccordionElement);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    let headerCart = document.querySelector("header-cart");
    this.headerCart = headerCart;
    this.openDrawer = this.openDrawer.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.justOpenedAt = 0;

    this.addEventListener("click", this.onClick);
  }

  connectedCallback() {
    document.addEventListener("click", this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onDocumentClick);
  }

  onClick(e) {
    const closeTrigger = e.target.closest("#close-drawer");
    if (closeTrigger) {
      this.closeDrawer();
      if (this.headerCart) {
        this.headerCart.changeState();
      }
    }
  }

  onDocumentClick(e) {
    if (Date.now() - this.justOpenedAt < 50) {
      return;
    }

    const isOpen = this.getAttribute("aria-hidden") !== "true";
    if (!isOpen) {
      return;
    }

    if (!this.contains(e.target)) {
      this.closeDrawer();
      if (this.headerCart) {
        this.headerCart.changeState();
      }
    }
  }

  openDrawer() {
    this.justOpenedAt = Date.now();
    if (this.headerCart) {
      this.headerCart.changeState();
    }
    this.removeAttribute("aria-hidden");
  }

  closeDrawer() {
    this.setAttribute("aria-hidden", "true");
  }

  async updateQuantity(itemId, quantity) {
    await fetch("/cart/change", {
      method: "post",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: itemId,
        quantity: quantity,
      }),
    }).then((res) => {
      console.log(res);
    });
    // Update the cart drawer after the quantity change
    await this.update();
  }

  async update() {
    const res = await fetch("/?section_id=cart-drawer");
    const text = await res.text();
    const html = document.createElement("div");
    html.innerHTML = text;

    const nextCartDrawer = html.querySelector("cart-drawer");
    if (nextCartDrawer) {
      this.innerHTML = nextCartDrawer.innerHTML;
    }

    // Update the cart count in the header
    const cartItemCount = document.getElementById("cart-item-count");
    const itemCountHelper = html.querySelector("#item-count-helper");
    if (cartItemCount && itemCountHelper) {
      // set to nothing if 0, otherwise set to the cart item count
      cartItemCount.textContent = itemCountHelper.textContent || "";
    }
  }

  async addToCart(form) {
    const requestBody = form instanceof FormData ? form : new FormData(form);

    await fetch("/cart/add", {
      method: "post",
      body: requestBody,
    });
    // update cart
    await this.update();
    this.openDrawer();
  }

  async addObjectToCart(object) {
    await fetch("/cart/add.js", {
      method: "post",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(object),
    });

    // update cart
    await this.update();
    this.openDrawer();
  }

  async removeLinkedSurchargeItems(groupId) {
    if (!groupId) {
      return 0;
    }

    const cartResponse = await fetch("/cart.js");
    const cartData = await cartResponse.json();
    const linkedSurchargeItems = cartData.items.filter((item) => {
      return (
        item?.properties?._is_surcharge === "true" &&
        item?.properties?._surcharge_group_id === groupId
      );
    });

    for (const item of linkedSurchargeItems) {
      await fetch("/cart/change.js", {
        method: "post",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: item.key,
          quantity: 0,
        }),
      });
    }

    return linkedSurchargeItems.length;
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.openDrawer = this.openDrawer.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
  }

  openDrawer() {
    this.setAttribute("open", "true");
  }

  closeDrawer() {
    this.removeAttribute("open");
  }
}

if (!customElements.get("menu-drawer")) {
  customElements.define("menu-drawer", MenuDrawer);
}

class CartItem extends HTMLElement {
  constructor() {
    super();
    // bind click event to button id decrease
    this.decreaseButton = this.querySelector("button[id='decrease']");
    this.increaseButton = this.querySelector("button[id='increase']");
    // this.quantity = parseInt(this.querySelector("p[id='quantity']").innerHTML);

    this.pid = this.getAttribute("data-product-id");
    // this.handleQuantity = this.handleQuantity.bind(this);

    //this.decreaseButton.addEventListener("click", this.handleQuantity);
    //this.increaseButton.addEventListener("click", this.handleQuantity);
  }

  // async handleQuantity(e) {
  //   console.log(e.target.id);
  //   if (e.target.id === "decrease") {
  //     if (parseInt(this.quantity) === 1) {
  //       console.log("too low");
  //     }
  //     if (parseInt(this.quantity) > 1) {
  //       document
  //         .querySelector("cart-drawer")
  //         .updateQuantity(this.pid, this.quantity - 1);
  //       console.log("decrease it by 1 bro");
  //     }
  //   }
  //   if (e.target.id === "increase") {
  //     document
  //       .querySelector("cart-drawer")
  //       .updateQuantity(this.pid, this.quantity + 1);
  //   }
  //   e.preventDefault();
  //   console.log(this.pid, this.quantity);
  // }
}

if (!customElements.get("cart-item")) {
  customElements.define("cart-item", CartItem);
}

class SlideShow extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.setupMarkup();
    this.initSwiper();
  }

  setupMarkup() {
    // Create base Swiper structure
    const swiperContainer = document.createElement("div");
    swiperContainer.classList.add("swiper");

    const wrapper = document.createElement("div");
    wrapper.classList.add("swiper-wrapper");

    // Move all existing children into swiper-slide wrappers
    Array.from(this.children).forEach((child) => {
      const slide = document.createElement("div");
      slide.classList.add("swiper-slide");
      slide.appendChild(child);
      wrapper.appendChild(slide);
    });

    swiperContainer.appendChild(wrapper);

    // Clear original content and append structured swiper
    this.innerHTML = "";
    this.appendChild(swiperContainer);
  }

  initSwiper() {
    const swiperEl = this.querySelector(".swiper");
    if (swiperEl) {
      // Build base options
      const options = {
        slidesPerView: "1.5",
        spaceBetween: 0,
        speed: 500,
        gap: 0,
        mousewheel: {
          forceToAxis: true,
        },
      };

      // Instantiate swiper
      this.swiper = new Swiper(swiperEl, options);
    }
  }
}

if (!customElements.get("swiper-gallery")) {
  customElements.define("swiper-gallery", SlideShow);
}

class RemoveButton extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.addEventListener("click", this.onClick);
  }

  async onClick(e) {
    e.preventDefault();

    const form = this.closest("form");
    const removeLink = e.target.closest("a[href]");
    const cartItem = this.closest("cart-item");
    const surchargeGroupId = cartItem?.dataset.surchargeGroupId || "";
    const isSurcharge = cartItem?.dataset.isSurcharge === "true";

    if (!removeLink) {
      return;
    }

    await fetch(removeLink.href, {
      method: "post",
      body: new FormData(form),
    });

    if (surchargeGroupId && !isSurcharge) {
      await document
        .querySelector("cart-drawer")
        .removeLinkedSurchargeItems(surchargeGroupId);
    }

    // Fetch the updated cart data to check if it's empty
    const response = await fetch("/cart.js");
    const cartData = await response.json();

    // If the cart is empty, close the drawer
    if (cartData.item_count === 0) {
      document.querySelector("cart-drawer").closeDrawer();
    }

    // Update the cart drawer after removing the item
    await document.querySelector("cart-drawer").update();
  }
}

if (!customElements.get("remove-button")) {
  customElements.define("remove-button", RemoveButton);
}

class SizeChart extends HTMLElement {
  constructor() {
    super();

    console.log("sizing....", this.getAttribute("data-size-chart"));

    // Parse the JSON data from the `data-size-chart` attribute
    this.sizeChart = JSON.parse(this.getAttribute("data-size-chart") || "[]");

    console.log(this.sizeChart, "size chart data");

    // Create a shadow DOM for encapsulation
    const shadow = this.attachShadow({ mode: "open" });

    // Apply styles and add the table to the shadow DOM
    const style = document.createElement("style");
    style.textContent = `
      * {
        font-family: "Corporate S Pro", Arial, sans-serif;
        font-size: 11px;
        font-style: normal;
        font-weight: 300;
        line-height: 109%;
        letter-spacing: 0.22px;
        text-align: left;
        margin-top: 0px;
        margin-bottom: -5px;
      }

      .table-header,
      .table-header th {
        text-transform: uppercase;
      }

      table {
        width: 100%;
        outline: 0;
        border-collapse: collapse;
      }

      th, td {
        width: 1%;
      }

      th {
        padding-bottom: 7.5px;
      }

      td {
        padding: 7.5px 0;
      }

      tr:last-child td {
        padding-bottom: 0;
      }

      .break {
        height: 0.5px;
        background-color: #D1D1D1;
      }
    `;

    // Generate the table and append it to the shadow DOM
    const table = this.generateTable();
    shadow.appendChild(style);
    shadow.appendChild(table);
  }

  // Method to generate the table based on JSON data
  generateTable() {
    const table = document.createElement("table");

    // Create headers from the first item in the data array
    if (this.sizeChart.length > 0) {
      const headers = Object.values(this.sizeChart[0]);
      const thead = document.createElement("thead");
      thead.classList.add("table-header");

      const headerRow = document.createElement("tr");
      headers.forEach((headerText) => {
        const th = document.createElement("th");
        th.textContent = headerText;
        headerRow.appendChild(th);
      });

      thead.appendChild(headerRow);
      table.appendChild(thead);

      // Create table body with data
      const tbody = document.createElement("tbody");
      this.sizeChart.slice(1).forEach((rowData) => {
        const row = document.createElement("tr");

        Object.values(rowData).forEach((cellData) => {
          const cell = document.createElement("td");
          cell.textContent = cellData;
          row.appendChild(cell);
        });

        tbody.appendChild(row);
      });
      table.appendChild(tbody);
    }

    return table;
  }
}

// Define the custom element if it’s not already defined
if (!customElements.get("size-chart")) {
  customElements.define("size-chart", SizeChart);
}
